import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ConcurrentUsageService } from '../../../services/concurrent-usage/concurrent-usage.service';
import { hydrateSlice, persistenceError, persistSlice, sliceHydrated, slicePersisted } from '../../persistence';
import {
    barcodeClipboardLoadedFromStorage,
    barcodeClipboardPersisted,
    barcodeClipboardStorageError,
    hydrateBarcodeClipboard,
    setBarcodeClipboardRequested,
} from '../actions/barcode-clipboard.actions';
import { BARCODE_CLIPBOARD_FEATURE_KEY, PaceBarcode } from '../barcode-clipboard-collection.state';

/** The concurrent-usage entry key the clipboard was stored under before this slice existed. */
const LEGACY_BARCODE_CLIPBOARD_CONCURRENT_STORAGE_KEY = 'pace-barcode-clipboard';

/**
 * Owns the barcode clipboard slice's write orchestration. The NgRx store is the source of truth:
 * mutations set the slice first (reducers), then this effect mirrors the collection to the
 * unified slice storage via the generic `persistSlice`/`hydrateSlice` layer (keyed by the
 * barcode-clipboard feature). The generic `slicePersisted`/`persistenceError`/`sliceHydrated`
 * outcomes are bridged back to the clipboard-specific settle actions so the facade's
 * correlation-id write contract is unchanged.
 */
@Injectable()
export class BarcodeClipboardEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly concurrentUsageService: ConcurrentUsageService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    /** Rehydrate the persisted clipboard from storage, via the generic layer. */
    public hydrateBarcodeClipboard$ = createEffect(() =>
        this.actions$.pipe(
            ofType(hydrateBarcodeClipboard),
            map(() => hydrateSlice({ featureKey: BARCODE_CLIPBOARD_FEATURE_KEY })),
        ),
    );

    /**
     * Bridge the generic hydrate result into the clipboard slice. A missing entry — nothing ever
     * written under the generic layer's key — falls back to a one-time read of
     * {@link LEGACY_BARCODE_CLIPBOARD_CONCURRENT_STORAGE_KEY}, so a user whose clipboard predates this
     * slice does not silently lose it. The fallback can be deleted once no device is expected to hold
     * data under that key any more.
     */
    public applyHydratedBarcodeClipboard$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === BARCODE_CLIPBOARD_FEATURE_KEY),
            switchMap(({ data }) => {
                if (data !== undefined) {
                    return of(barcodeClipboardLoadedFromStorage({ barcodes: data as PaceBarcode[] }));
                }
                return this.readLegacyClipboard$();
            }),
        ),
    );

    /** The store already holds the clipboard; mirror it to storage via the generic layer. */
    public persistBarcodeClipboard$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setBarcodeClipboardRequested),
            map(({ correlationId }) => persistSlice({ featureKey: BARCODE_CLIPBOARD_FEATURE_KEY, correlationId })),
        ),
    );

    /** Bridge the generic persist success back to the clipboard settle action. */
    public barcodeClipboardPersisted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(slicePersisted),
            filter(({ featureKey }) => featureKey === BARCODE_CLIPBOARD_FEATURE_KEY),
            map(({ correlationId }) => barcodeClipboardPersisted({ correlationId })),
        ),
    );

    /** Bridge the generic persist/hydrate failure back to the clipboard settle action. */
    public barcodeClipboardPersistError$ = createEffect(() =>
        this.actions$.pipe(
            ofType(persistenceError),
            filter(({ featureKey }) => featureKey === BARCODE_CLIPBOARD_FEATURE_KEY),
            map(({ error, correlationId }) => {
                this.log.error(`BarcodeClipboardEffects: barcodeClipboardPersistError$: ${error}`);
                return barcodeClipboardStorageError({ error, correlationId });
            }),
        ),
    );

    /**
     * One-time migration read of the `pace-barcode-clipboard` concurrent-usage entry, which holds
     * the clipboard as plain JSON `PaceBarcode[]`. A missing entry or a read/parse failure yields an
     * empty clipboard rather than an error: an unreadable quick-pick list is not worth failing over.
     *
     * @returns an observable emitting the load action, populated from that entry or empty
     */
    private readLegacyClipboard$(): Observable<ReturnType<typeof barcodeClipboardLoadedFromStorage>> {
        return this.concurrentUsageService.readDataForKey(LEGACY_BARCODE_CLIPBOARD_CONCURRENT_STORAGE_KEY).pipe(
            map(([entry]) => {
                if (!entry?.value) {
                    return barcodeClipboardLoadedFromStorage({ barcodes: [] });
                }
                return barcodeClipboardLoadedFromStorage({ barcodes: JSON.parse(entry.value) as PaceBarcode[] });
            }),
            catchError((error) => {
                this.log.error(
                    `BarcodeClipboardEffects: readLegacyClipboard$: failed to parse legacy clipboard: ${error}`,
                );
                return of(barcodeClipboardLoadedFromStorage({ barcodes: [] }));
            }),
        );
    }
}
